import type { MalwareState } from '../malware-detection/useMalwareData';
import type {
  WazuhMalwareAgentSummaryData,
  WazuhMalwareDetectionItem,
} from 'src/types/mindtime/wazuhMalware';
import { previewPaged } from './previewUtils';

export const PREVIEW_MALWARE_IDS = ['preview-malware-1', 'preview-malware-2', 'preview-malware-3'] as const;

const PREVIEW_DETECTIONS: WazuhMalwareDetectionItem[] = [
  {
    id: 'preview-malware-1',
    timestamp: '2026-08-28T10:15:00.000Z',
    threatName: 'Trojan.GenericKD',
    malwareType: 'trojan',
    malwareFamily: 'Generic',
    severity: 'high',
    status: 'open',
    endpoint: 'FIN-SRV-02',
    organization: 'Acme Financial',
    systemWazuhAgentId: 'preview-endpoint-1',
    wazuhAgentId: '001',
    hostName: 'fin-srv-02.acme.local',
  },
  {
    id: 'preview-malware-2',
    timestamp: '2026-08-28T09:42:00.000Z',
    threatName: 'Emotet Loader',
    malwareType: 'worm',
    malwareFamily: 'Emotet',
    severity: 'critical',
    status: 'quarantined',
    endpoint: 'HR-PC-17',
    organization: 'Vertex Solutions',
    systemWazuhAgentId: 'preview-endpoint-2',
    wazuhAgentId: '002',
    hostName: 'hr-pc-17.vertex.local',
  },
  {
    id: 'preview-malware-3',
    timestamp: '2026-08-27T18:20:00.000Z',
    threatName: 'Suspicious Script',
    malwareType: 'script',
    severity: 'medium',
    status: 'resolved',
    endpoint: 'APP-SERVER-01',
    organization: 'Nexus Dynamics',
    systemWazuhAgentId: 'preview-endpoint-3',
    wazuhAgentId: '003',
    hostName: 'app-server-01.nexus.local',
  },
];

export const getMalwarePreviewState = (): MalwareState => ({
  loading: false,
  summary: {
    totalDetections: 18,
    criticalThreats: 3,
    detectionsToday: 5,
    quarantined: 4,
    remediationPercent: 72,
  },
  items: previewPaged(PREVIEW_DETECTIONS, 0, 15),
  endpoints: previewPaged(
    [
      {
        systemWazuhAgentId: 'preview-endpoint-2',
        endpoint: 'HR-PC-17',
        hostName: 'hr-pc-17.vertex.local',
        organization: 'Vertex Solutions',
        detections: 6,
        activeThreats: 2,
        highestSeverity: 'critical',
        lastDetected: '2026-08-28T09:42:00.000Z',
      },
      {
        systemWazuhAgentId: 'preview-endpoint-1',
        endpoint: 'FIN-SRV-02',
        hostName: 'fin-srv-02.acme.local',
        organization: 'Acme Financial',
        detections: 4,
        activeThreats: 1,
        highestSeverity: 'high',
        lastDetected: '2026-08-28T10:15:00.000Z',
      },
    ],
    0,
    10,
  ),
  categories: {
    categories: { trojan: 8, worm: 4, script: 3, ransomware: 2, adware: 1 },
  },
  facets: { types: ['trojan', 'worm', 'script', 'ransomware'] },
});

export const getMalwareDetailPreview = (id: string): WazuhMalwareDetectionItem | null =>
  PREVIEW_DETECTIONS.find((row) => row.id === id) ?? PREVIEW_DETECTIONS[0] ?? null;

export const getAgentMalwarePreview = (
  systemWazuhAgentId: string,
): { summary: WazuhMalwareAgentSummaryData; items: WazuhMalwareDetectionItem[] } => {
  const agentId = systemWazuhAgentId.trim();
  const items = PREVIEW_DETECTIONS.filter(
    (row) => row.systemWazuhAgentId === agentId || row.wazuhAgentId === agentId,
  );
  const list = items.length ? items : PREVIEW_DETECTIONS.slice(0, 1);
  const today = new Date();
  const detectionsToday = list.filter((row) => {
    const date = new Date(row.timestamp);
    return (
      date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate()
    );
  }).length;

  return {
    summary: {
      systemWazuhAgentId: agentId,
      totalDetections: list.length,
      detectionsToday,
    },
    items: list,
  };
};
